/**
 * Developer tools for managing feature flags
 */

import { FeatureFlags, setFeatureFlag, isFeatureEnabled } from './featureFlags';

declare global {
  interface Window {
    hotelspot?: {
      enableRegistration: () => void;
      disableRegistration: () => void;
      checkRegistration: () => boolean;
      toggleRegistration: () => boolean;
    };
  }
}

/**
 * Initialize developer tools in the browser console
 */
export const initDevTools = (): void => {
  if (typeof window === 'undefined') return;

  window.hotelspot = {
    /**
     * Enable registration flow (Google sign-in and sign-up page)
     */
    enableRegistration: () => {
      setFeatureFlag(FeatureFlags.REGISTER_FLOW, true);
      console.log('✅ Registration flow enabled. Refresh the page to see changes.');
      return undefined;
    },

    /**
     * Disable registration flow (Google sign-in and sign-up page)
     */
    disableRegistration: () => {
      setFeatureFlag(FeatureFlags.REGISTER_FLOW, false);
      console.log('❌ Registration flow disabled. Refresh the page to see changes.');
      return undefined;
    },

    /**
     * Check if registration flow is enabled
     */
    checkRegistration: () => {
      const enabled = isFeatureEnabled(FeatureFlags.REGISTER_FLOW);
      console.log(`Registration flow is ${enabled ? '✅ ENABLED' : '❌ DISABLED'}`);
      return enabled;
    },

    /**
     * Toggle registration flow
     */
    toggleRegistration: () => {
      const currentState = isFeatureEnabled(FeatureFlags.REGISTER_FLOW);
      const newState = !currentState;
      setFeatureFlag(FeatureFlags.REGISTER_FLOW, newState);
      console.log(`Registration flow toggled: ${newState ? '✅ ENABLED' : '❌ DISABLED'}. Refresh the page to see changes.`);
      return newState;
    },
  };

  // Log info in development
  if (import.meta.env.DEV) {
    console.log('%c🏨 HotelSpot Dev Tools Available', 'color: #E07B2D; font-size: 14px; font-weight: bold;');
    console.log('Use window.hotelspot to access developer tools:');
    console.log('  • enableRegistration() - Enable Google sign-in and registration');
    console.log('  • disableRegistration() - Disable Google sign-in and registration');
    console.log('  • checkRegistration() - Check current state');
    console.log('  • toggleRegistration() - Toggle state');
  }
};